// cc-web-learning/src/Components/FairPlayAlertModal/index.js
//
// FairPlay Phase 3 — User-facing appeal popup (Web version).
// Mirror of cc-learning-app/src/Components/FairPlayAlertModal/index.tsx
// Written in JS (no TypeScript) following cc-web-learning conventions.
//
// Props:
//   alerts      — sorted DESC by created_at, max 3 items from server
//   userProfile — { fide_id, lic_name, cdc_name, username } from cc_users
//   onMarkAsRead(messageId) — async callback
//   onAppealSubmit(payload) — async callback
//   onDismiss() — called when all alerts are cleared

import React, {useState, useMemo} from 'react';
import {
  Modal,
  View,
  TextInput,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCColors from '@/Utils/CCColors';
import {FontSizes, hs, vs, ms, Spacing} from '@/Utils/Dimensions';
import useFairplayAppealApi from '@/Hooks/FairPlayApi/useFairplayAppealApi';

// ─── Constants ────────────────────────────────────────────────────────────────
const FAIRPLAY_MAX_ALERT_QUEUE = 3;
const FAIRPLAY_APPEAL_MAX_CHARS = 500;
const FAIRPLAY_APPEAL_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours in ms

// ─── Component ────────────────────────────────────────────────────────────────
function FairPlayAlertModal({
  alerts = [],
  userProfile = {},
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [appealReason, setAppealReason] = useState('');
  const [fideInput, setFideInput] = useState('');
  const [lichessInput, setLichessInput] = useState('');
  const [chesscomInput, setChesscomInput] = useState('');
  const [wantsExpertCall, setWantsExpertCall] = useState(false);

  // ── Hook: fairplay appeal API ──────────────────────────────────────────────
  const {mutate: submitAppeal, isLoading: isSubmitting} = useFairplayAppealApi(
    () => {
      advanceQueue();
    },
    error => {
      console.error('[FairPlayAlertModal] API error:', error);
      advanceQueue();
    },
  );

  const cappedAlerts = useMemo(
    () => alerts.slice(0, FAIRPLAY_MAX_ALERT_QUEUE),
    [alerts],
  );

  const alert = cappedAlerts[currentIndex];
  const isVisible =
    cappedAlerts.length > 0 && currentIndex < cappedAlerts.length;

  const isWithin24h = useMemo(() => {
    if (!alert?.created_at) return false;
    return (
      Date.now() - new Date(alert.created_at).getTime() <
      FAIRPLAY_APPEAL_WINDOW_MS
    );
  }, [alert]);

  const showFide = !userProfile?.fide_id;
  const showLichess = !userProfile?.lic_name;
  const showChesscom = !userProfile?.cdc_name;

  const advanceQueue = () => {
    setAppealReason('');
    setFideInput('');
    setLichessInput('');
    setChesscomInput('');
    setWantsExpertCall(false);
    setCurrentIndex(prev => prev + 1);
  };

  const handleMarkAsRead = () => {
    if (!alert || isSubmitting) return;
    submitAppeal({message_sent_id: alert.id, action: 'mark_as_read'});
  };

  const handleAppeal = () => {
    if (!alert || isSubmitting || !isWithin24h) return;
    const payload = {
      message_sent_id: alert.id,
      action: 'appeal',
      appeal_reason: appealReason.slice(0, FAIRPLAY_APPEAL_MAX_CHARS),
      wants_expert_call: wantsExpertCall,
    };
    if (showFide && fideInput.trim()) {
      const parsed = parseInt(fideInput.trim(), 10);
      if (!isNaN(parsed)) payload.fide_id = parsed;
    }
    if (showLichess && lichessInput.trim()) {
      payload.lichess_username = lichessInput.trim();
    }
    if (showChesscom && chesscomInput.trim()) {
      payload.chesscom_username = chesscomInput.trim();
    }
    submitAppeal(payload);
  };

  if (!isVisible || !alert) return null;

  const totalAlerts = cappedAlerts.length;
  const displayUsername =
    alert.player_username || userProfile?.username || 'User';
  const roundsDisplay = alert.rounds?.split(',').join(', ') || alert.rounds;

  return (
    <Modal
      animationType="fade"
      transparent
      visible={isVisible}
      onRequestClose={() => {
        // Intentionally blocked — user must choose Mark as Read or Appeal
      }}>
      <View style={styles.overlay}>
        <View style={styles.container}>
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.scroll}>
            {/* ── Header ── */}
            <View style={styles.header}>
              <View style={styles.warningBadge}>
                <CCText style={styles.warningEmoji}>⚠️</CCText>
                <CCText style={styles.headerTitle}>FairPlay Alert</CCText>
              </View>
              {totalAlerts > 1 && (
                <CCText style={styles.queueCounter}>
                  {currentIndex + 1} of {totalAlerts}
                </CCText>
              )}
            </View>

            <View style={styles.divider} />

            {/* ── Message body ── */}
            <CCText style={styles.bodyText}>Dear {displayUsername},</CCText>
            <CCText style={{...styles.bodyText, marginTop: Spacing.xxSmall}}>
              Our FairPlay System has flagged your{' '}
              <CCText style={styles.bold}>{alert.tournament_name}</CCText>{' '}
              tournament games for round(s){' '}
              <CCText style={styles.bold}>{roundsDisplay}</CCText>.
            </CCText>
            {isWithin24h && (
              <>
                <CCText style={{...styles.bodyText, marginTop: Spacing.xxSmall}}>
                  Please let us know if you want to appeal.
                </CCText>

                {/* ── External IDs ── */}
                {(showFide || showLichess || showChesscom) && (
                  <View style={styles.section}>
                    <CCText style={styles.sectionLabel}>
                      Help us verify your identity (optional):
                    </CCText>
                    {showFide && (
                      <TextInput
                        style={styles.input}
                        placeholder="FIDE ID"
                        placeholderTextColor={CCColors.TextColor.Grey}
                        keyboardType="numeric"
                        value={fideInput}
                        onChangeText={setFideInput}
                      />
                    )}
                    {showChesscom && (
                      <TextInput
                        style={styles.input}
                        placeholder="Chess.com username"
                        placeholderTextColor={CCColors.TextColor.Grey}
                        autoCapitalize="none"
                        value={chesscomInput}
                        onChangeText={setChesscomInput}
                      />
                    )}
                    {showLichess && (
                      <TextInput
                        style={styles.input}
                        placeholder="Lichess username"
                        placeholderTextColor={CCColors.TextColor.Grey}
                        autoCapitalize="none"
                        value={lichessInput}
                        onChangeText={setLichessInput}
                      />
                    )}
                  </View>
                )}

                {/* ── Appeal reason ── */}
                <View style={styles.section}>
                  <CCText style={styles.sectionLabel}>
                    Reason for appeal:
                  </CCText>
                  <TextInput
                    style={styles.reasonInput}
                    placeholder="Tell us why you believe this flag is incorrect..."
                    placeholderTextColor={CCColors.TextColor.Grey}
                    multiline
                    numberOfLines={4}
                    maxLength={FAIRPLAY_APPEAL_MAX_CHARS}
                    value={appealReason}
                    onChangeText={setAppealReason}
                    textAlignVertical="top"
                  />
                  <CCText style={styles.charCount}>
                    {appealReason.length} / {FAIRPLAY_APPEAL_MAX_CHARS}
                  </CCText>
                </View>

                {/* ── Expert call checkbox ── */}
                <TouchableOpacity
                  style={styles.checkboxRow}
                  onPress={() => setWantsExpertCall(prev => !prev)}
                  activeOpacity={0.7}>
                  <View
                    style={[
                      styles.checkbox,
                      wantsExpertCall && styles.checkboxActive,
                    ]}>
                    {wantsExpertCall && (
                      <CCText style={styles.checkmark}>✓</CCText>
                    )}
                  </View>
                  <CCText style={styles.checkboxLabel}>
                    I'd like to talk to a FairPlay Expert
                  </CCText>
                </TouchableOpacity>
              </>
            )}

            {/* ── Appeal window notice ── */}
            {!isWithin24h && (
              <View style={styles.windowClosed}>
                <CCText style={styles.windowClosedText}>
                  ⏱ Appeal window has closed (24 hrs from notification). You
                  can still mark this as read.
                </CCText>
              </View>
            )}

            {/* ── Action buttons ── */}
            <View style={styles.buttonRow}>
              <CCButton
                onPress={handleMarkAsRead}
                style={[styles.button, styles.markReadBtn]}
                disabled={isSubmitting}>
                <CCText style={styles.markReadText}>Mark as Read</CCText>
              </CCButton>

              <CCButton
                onPress={handleAppeal}
                style={[
                  styles.button,
                  styles.appealBtn,
                  !isWithin24h && styles.appealBtnDisabled,
                ]}
                disabled={isSubmitting || !isWithin24h}>
                <CCText style={styles.appealText}>Appeal</CCText>
              </CCButton>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

export default FairPlayAlertModal;

// ─── Styles ─────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: CCColors.Modal.Background,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: hs(16),
  },
  container: {
    backgroundColor: CCColors.Primary.lightBrown,
    borderRadius: ms(16),
    width: '100%',
    maxWidth: hs(420),
    maxHeight: vs(640),
    borderWidth: 1.5,
    borderColor: CCColors.Red,
    shadowColor: CCColors.Black,
    shadowOffset: {width: 0, height: vs(4)},
    shadowOpacity: 0.18,
    shadowRadius: ms(8),
    elevation: 8,
  },
  scroll: {
    padding: hs(20),
    paddingBottom: vs(24),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: vs(4),
  },
  warningBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CCColors.RedLight,
    paddingHorizontal: hs(10),
    paddingVertical: vs(4),
    borderRadius: ms(20),
  },
  warningEmoji: {
    fontSize: FontSizes['14'],
    marginRight: hs(4),
  },
  headerTitle: {
    fontSize: FontSizes['14'],
    color: CCColors.Red,
    fontWeight: '700',
  },
  queueCounter: {
    fontSize: FontSizes['12'],
    color: CCColors.TextColor.Grey,
  },
  divider: {
    height: 1,
    backgroundColor: CCColors.BorderLight,
    marginVertical: vs(12),
  },
  bodyText: {
    fontSize: FontSizes['14'],
    color: CCColors.TextColor.Dark,
    lineHeight: vs(22),
  },
  bold: {
    fontWeight: '700',
    color: CCColors.TextColor.Dark,
  },
  section: {
    marginTop: vs(16),
  },
  sectionLabel: {
    fontSize: FontSizes['12'],
    color: CCColors.TextColor.Grey,
    marginBottom: vs(6),
    fontWeight: '600',
  },
  input: {
    borderWidth: 1,
    borderColor: CCColors.TextBoxes.Borders,
    borderRadius: ms(8),
    paddingHorizontal: hs(12),
    paddingVertical: vs(8),
    fontSize: FontSizes['13'],
    color: CCColors.TextColor.Dark,
    backgroundColor: CCColors.White,
    marginBottom: vs(8),
  },
  reasonInput: {
    borderWidth: 1,
    borderColor: CCColors.TextBoxes.Borders,
    borderRadius: ms(8),
    paddingHorizontal: hs(12),
    paddingVertical: vs(10),
    fontSize: FontSizes['13'],
    color: CCColors.TextColor.Dark,
    backgroundColor: CCColors.White,
    minHeight: vs(90),
  },
  charCount: {
    fontSize: FontSizes['11'],
    color: CCColors.TextColor.Grey,
    textAlign: 'right',
    marginTop: vs(4),
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: vs(16),
    gap: hs(10),
  },
  checkbox: {
    width: hs(20),
    height: hs(20),
    borderWidth: 2,
    borderColor: CCColors.Green,
    borderRadius: ms(4),
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: CCColors.White,
  },
  checkboxActive: {
    backgroundColor: CCColors.Green,
  },
  checkmark: {
    color: CCColors.White,
    fontSize: FontSizes['12'],
    fontWeight: '700',
    lineHeight: hs(16),
  },
  checkboxLabel: {
    flex: 1,
    fontSize: FontSizes['13'],
    color: CCColors.TextColor.Dark,
  },
  windowClosed: {
    marginTop: vs(12),
    backgroundColor: CCColors.LightYellow,
    borderRadius: ms(8),
    padding: hs(10),
  },
  windowClosedText: {
    fontSize: FontSizes['12'],
    color: CCColors.BrownShade2,
  },
  buttonRow: {
    flexDirection: 'row',
    marginTop: vs(20),
    gap: hs(10),
  },
  button: {
    flex: 1,
    paddingVertical: vs(10),
    borderRadius: ms(10),
    justifyContent: 'center',
    alignItems: 'center',
  },
  markReadBtn: {
    backgroundColor: CCColors.Primary.Default,
    borderWidth: 0,
  },
  appealBtn: {
    backgroundColor: CCColors.Green,
    borderWidth: 0,
  },
  appealBtnDisabled: {
    backgroundColor: CCColors.Grey.Shade200,
  },
  markReadText: {
    fontSize: FontSizes['13'],
    color: CCColors.TextColor.Dark,
    fontWeight: '700',
  },
  appealText: {
    fontSize: FontSizes['13'],
    color: CCColors.White,
    fontWeight: '700',
  },
});
